import React, { useEffect, useCallback, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import {
  Search as SearchIcon,
  Copy,
  Check,
  X,
  Edit,
  ArrowUpRight,
  Loader,
} from 'lucide-react';
import DOMPurify from 'dompurify';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';

import { FileType } from './types';

interface ResultsProps {
  completion: string;
  isLoading: boolean;
  stage: string;
  loadingStage: string | null;
  selectedFiles: string[];
  files: {
    data: FileType[];
  };
  activeSource: {
    fileId?: string;
    timestamp?: string;
  } | null;
  citationCount: { [key: string]: number };
  hideReferences: boolean;
  handleSourceClick: (fileId: string, timestamp: string) => void;
  setCitationCount: React.Dispatch<
    React.SetStateAction<{ [key: string]: number }>
  >;
  resultSearchTerm?: string;
  setResultSearchTerm?: React.Dispatch<React.SetStateAction<string>>;
  onCopyContent?: (content: string) => void;
  copySuccess?: boolean;
  onClearCompletion?: () => void;
  onEditQuery?: () => void;
}

export const formatTimestamp = (seconds: string): string => {
  const totalSeconds = parseInt(seconds, 10);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
  return `${minutes
    .toString()
    .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Highlight search terms in text
const highlightSearchTerm = (
  text: string | React.ReactNode,
  searchTerm?: string,
): React.ReactNode => {
  if (!searchTerm || typeof text !== 'string') return text;

  try {
    // Handle multiple words in search term
    const searchTerms = searchTerm
      .trim()
      .split(/\s+/)
      .filter((term) => term.length > 0);

    // If no valid search terms, return the original text
    if (searchTerms.length === 0) return text;

    // Create a single regex pattern with all search terms as alternatives
    const escapedTerms = searchTerms.map((term) =>
      term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
    );

    const pattern = escapedTerms.join('|');
    const regex = new RegExp(`(${pattern})`, 'gi');

    // Split the text by the search terms, keeping the delimiters
    const parts = text.split(regex);

    // If no matches found (split returns single element), return original text
    if (parts.length === 1) return text;

    // Build the result, highlighting every odd-indexed part (the matches)
    return parts.map((part, i) => {
      // Even indices are non-matches, odd indices are matches
      if (i % 2 === 0) {
        return part;
      }
      // This is a match, highlight it
      return (
        <span
          key={i}
          style={{
            backgroundColor: '#fff176',
          }}
        >
          {part}
        </span>
      );
    });
  } catch (error) {
    console.error('Search highlighting error:', error);
    return text;
  }
};

type MarkdownLinkProps = {
  href?: string;
  children?: React.ReactNode;
};

// Custom components for ReactMarkdown
const createMarkdownComponents = (
  activeSource: {
    fileId?: string;
    timestamp?: string;
  } | null,
  files: {
    data: FileType[];
  },
  handleSourceClick: (fileId: string, timestamp: string) => void,
  citationCount: { [key: string]: number },
  setCitationCount: React.Dispatch<
    React.SetStateAction<{ [key: string]: number }>
  >,
  hideReferences: boolean,
  resultSearchTerm?: string,
) => ({
  h1: ({ children, ...props }: React.ComponentPropsWithoutRef<'h1'>) => (
    <h1
      style={{
        fontSize: '1.125rem',
        fontWeight: 'bold',
        marginBottom: '0.5rem',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </h1>
  ),
  h2: ({ children, ...props }: React.ComponentPropsWithoutRef<'h2'>) => (
    <h2
      style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: '0.5rem' }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </h2>
  ),
  h3: ({ children, ...props }: React.ComponentPropsWithoutRef<'h3'>) => (
    <h3
      style={{
        fontSize: '0.9375rem',
        fontWeight: 'bold',
        marginBottom: '0.375rem',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </h3>
  ),
  h4: ({ children, ...props }: React.ComponentPropsWithoutRef<'h4'>) => (
    <h4
      style={{
        fontSize: '0.875rem',
        fontWeight: 'bold',
        marginBottom: '0.375rem',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </h4>
  ),
  h5: ({ children, ...props }: React.ComponentPropsWithoutRef<'h5'>) => (
    <h5
      style={{
        fontSize: '0.8125rem',
        fontWeight: 'bold',
        marginBottom: '0.25rem',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </h5>
  ),
  h6: ({ children, ...props }: React.ComponentPropsWithoutRef<'h6'>) => (
    <h6
      style={{
        fontSize: '0.75rem',
        fontWeight: 'bold',
        marginBottom: '0.25rem',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </h6>
  ),
  p: ({ children, ...props }: React.ComponentPropsWithoutRef<'p'>) => (
    <p
      style={{
        fontSize: '0.875rem',
        lineHeight: '1.5',
        marginBottom: '0.75rem',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </p>
  ),
  strong: ({
    children,
    ...props
  }: React.ComponentPropsWithoutRef<'strong'>) => (
    <strong
      style={{
        fontWeight: 600,
        display: 'inline',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </strong>
  ),
  ul: ({ children, ...props }: React.ComponentPropsWithoutRef<'ul'>) => (
    <ul
      style={{
        paddingLeft: '1.5em',
        marginTop: '0.5em',
        marginBottom: '0.5em',
        listStyleType: 'disc',
      }}
      {...props}
    >
      {children}
    </ul>
  ),
  ol: ({ children, ...props }: React.ComponentPropsWithoutRef<'ol'>) => (
    <ol
      style={{
        paddingLeft: '1.5em',
        marginTop: '0.5em',
        marginBottom: '0.5em',
      }}
      {...props}
    >
      {children}
    </ol>
  ),
  li: ({ children, ...props }: React.ComponentPropsWithoutRef<'li'>) => (
    <li
      style={{
        fontSize: '0.875rem',
        marginBottom: '0.375rem',
        marginTop: '0.375rem',
        lineHeight: '1.5',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </li>
  ),
  table: ({ children, ...props }: React.ComponentPropsWithoutRef<'table'>) => (
    <table style={{ borderCollapse: 'collapse', width: '100%' }} {...props}>
      {children}
    </table>
  ),
  th: ({ children, ...props }: React.ComponentPropsWithoutRef<'th'>) => (
    <th
      style={{
        fontSize: '0.875rem',
        border: '1px solid hsl(var(--border))',
        padding: '6px',
        backgroundColor: 'hsl(var(--muted))',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </th>
  ),
  td: ({ children, ...props }: React.ComponentPropsWithoutRef<'td'>) => (
    <td
      style={{
        fontSize: '0.875rem',
        border: '1px solid hsl(var(--border))',
        padding: '6px',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </td>
  ),
  code: ({ children, ...props }: React.ComponentPropsWithoutRef<'code'>) => (
    <code
      style={{
        backgroundColor: 'hsl(var(--muted))',
        padding: '2px 4px',
        borderRadius: '4px',
        fontSize: '0.8125rem',
      }}
      {...props}
    >
      {children}
    </code>
  ),
  blockquote: ({
    children,
    ...props
  }: React.ComponentPropsWithoutRef<'blockquote'>) => (
    <blockquote
      style={{
        fontSize: '0.875rem',
        borderLeft: '3px solid hsl(var(--border))',
        marginLeft: 0,
        paddingLeft: '12px',
        fontStyle: 'italic',
        marginBottom: '0.75rem',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </blockquote>
  ),
  hr: ({ ...props }: React.ComponentPropsWithoutRef<'hr'>) => (
    <hr
      style={{
        border: 'none',
        borderTop: '1px solid hsl(var(--border))',
        margin: '1em 0',
      }}
      {...props}
    />
  ),
  a: ({ href, children }: MarkdownLinkProps) => {
    if (href?.includes('@')) {
      if (hideReferences) return null;
      const [fileId, timestamp] = href.split('@');
      const isActive =
        activeSource?.fileId === fileId &&
        activeSource?.timestamp === timestamp;
      const file = files.data.find((f) => f._id === fileId);

      const PreviewContent = () => {
        if (!file) {
          return (
            <div className="text-sm text-muted-foreground">File not found</div>
          );
        }

        const timestampSeconds = timestamp ? parseInt(timestamp, 10) : 0;

        return (
          <div className="w-96">
            <div className="mb-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">
                  Source Preview
                </span>
              </div>
              <div className="text-sm text-muted-foreground">
                {file.name} at {formatTimestamp(timestamp || '0')}
              </div>
            </div>

            {file.streams?.includes('video') ? (
              <div className="relative h-48 overflow-hidden bg-black rounded">
                <video
                  controls
                  src={file.url}
                  className="w-full h-full object-contain"
                  onLoadedMetadata={(e) => {
                    const video = e.target as HTMLVideoElement;
                    video.currentTime = timestampSeconds;
                  }}
                >
                  <source src={file.url} type="video/mp4" />
                  <track kind="captions" />
                  Your browser does not support the video tag.
                </video>
              </div>
            ) : (
              <div className="w-full">
                <audio
                  controls
                  src={file.url}
                  className="w-full"
                  onLoadedMetadata={(e) => {
                    const audio = e.target as HTMLAudioElement;
                    audio.currentTime = timestampSeconds;
                  }}
                >
                  <source src={file.url} type="audio/mp3" />
                  <track kind="captions" />
                  Your browser does not support the audio tag.
                </audio>
              </div>
            )}
          </div>
        );
      };

      return (
        <Popover
          open={isActive}
          onOpenChange={(open) => {
            if (open) {
              handleSourceClick(fileId, timestamp);
            } else {
              handleSourceClick('', '');
            }
          }}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                'inline cursor-pointer align-middle transition-transform duration-150 hover:scale-110 border-0 bg-transparent p-0',
              )}
              title={`Show source: ${file?.name || fileId} at ${formatTimestamp(timestamp)}`}
            >
              <ArrowUpRight
                size={18}
                className={cn(
                  'relative -top-0.5 inline-block px-0.5 align-middle transition-colors duration-150',
                  isActive ? 'text-primary' : 'text-gray-400',
                )}
                strokeWidth={1.5}
              />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-4" side="top" align="center">
            <PreviewContent />
          </PopoverContent>
        </Popover>
      );
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline"
      >
        {highlightSearchTerm(children as string, resultSearchTerm)}
      </a>
    );
  },
});

const Results: React.FC<ResultsProps> = ({
  completion,
  isLoading,
  stage,
  selectedFiles,
  files,
  activeSource,
  citationCount,
  hideReferences,
  handleSourceClick,
  setCitationCount,
  resultSearchTerm: externalResultSearchTerm,
  setResultSearchTerm: externalSetResultSearchTerm,
  onCopyContent,
  copySuccess,
  onClearCompletion,
  onEditQuery,
}) => {
  const [internalResultSearchTerm, setInternalResultSearchTerm] = useState('');
  const [matchCount, setMatchCount] = useState(0);
  const [showSearchInput, setShowSearchInput] = useState(false);

  // Use external search term if provided, otherwise use internal
  const resultSearchTerm = externalResultSearchTerm ?? internalResultSearchTerm;
  const setResultSearchTerm =
    externalSetResultSearchTerm ?? setInternalResultSearchTerm;

  // Function to strip HTML tags from text
  const stripHtmlTags = (html: string): string => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Function to process markdown text and highlight all search terms
  const processMarkdownWithHighlights = useCallback(
    (text: string, searchTermText: string): string => {
      if (!searchTermText || !text) return text;

      try {
        // First convert markdown to HTML
        let html = text
          // Handle headers
          .replace(/^### (.*$)/gim, '<h3>$1</h3>')
          .replace(/^## (.*$)/gim, '<h2>$1</h2>')
          .replace(/^# (.*$)/gim, '<h1>$1</h1>')
          // Handle emphasis (bold, italic)
          .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
          .replace(/\*(.*?)\*/gim, '<em>$1</em>')
          .replace(/__(.*?)__/gim, '<strong>$1</strong>')
          .replace(/_(.*?)_/gim, '<em>$1</em>')
          // Handle lists
          .replace(/^\* (.*$)/gim, '<ul><li>$1</li></ul>')
          .replace(/^- (.*$)/gim, '<ul><li>$1</li></ul>')
          .replace(/^(\d+)\. (.*$)/gim, '<ol><li>$2</li></ol>')
          // Handle links
          .replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2">$1</a>')
          // Handle code blocks
          .replace(/```([\s\S]*?)```/gim, '<pre><code>$1</code></pre>')
          .replace(/`([^`]+)`/gim, '<code>$1</code>')
          // Handle blockquotes
          .replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>')
          // Handle paragraphs
          .replace(/\n\s*\n/gim, '</p><p>')
          .replace(/\n/gim, ' ')
          // Clean up list tags
          .replace(/<\/ul><ul>/gim, '')
          .replace(/<\/ol><ol>/gim, '');

        // Wrap in paragraph tags if not already wrapped
        if (!html.startsWith('<')) {
          html = `<p>${html}</p>`;
        }

        // Process search terms for highlighting
        const searchTerms = searchTermText
          .trim()
          .split(/\s+/)
          .filter((term) => term.length > 0);
        if (searchTerms.length === 0) return html;

        // Create regex pattern for all search terms
        const escapedTerms = searchTerms.map((term) =>
          term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
        );
        const pattern = `(${escapedTerms.join('|')})`;
        const regex = new RegExp(pattern, 'gi');

        // Create a temporary div to work with the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        // Function to recursively process nodes and highlight matches
        const processNode = (node: Node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            const nodeText = node.textContent || '';
            if (nodeText.trim() && regex.test(nodeText)) {
              const parts = nodeText.split(regex);
              if (parts.length > 1) {
                const fragment = document.createDocumentFragment();
                parts.forEach((part, index) => {
                  if (index % 2 === 0) {
                    // Non-match
                    if (part) {
                      fragment.appendChild(document.createTextNode(part));
                    }
                  } else if (part) {
                    // Match - add highlighted span
                    const span = document.createElement('span');
                    span.textContent = part;
                    span.style.backgroundColor = '#fff176';
                    fragment.appendChild(span);
                  }
                });
                node.parentNode?.replaceChild(fragment, node);
              }
            }
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            // Skip highlighting in code blocks and pre elements
            if (!['CODE', 'PRE'].includes(node.nodeName)) {
              Array.from(node.childNodes).forEach(processNode);
            }
          }
        };

        // Process all nodes
        Array.from(tempDiv.childNodes).forEach(processNode);

        // Get the processed HTML
        html = tempDiv.innerHTML;

        // Final clean-up
        html = html.replace(/→/g, '').replace(/\[\]\([0-9a-f-]+@\d+\)/g, '');

        // Sanitize HTML to prevent XSS
        return DOMPurify.sanitize(html);
      } catch (error) {
        console.error('Error processing markdown with highlights:', error);
        return text;
      }
    },
    [],
  );

  // Fix malformed references in the completion text
  const fixMalformedReferences = useCallback((text: string): string => {
    if (!text) return '';

    // Fix UUID-based references with missing closing parenthesis: [→](uuid@timestamp] -> [→](uuid@timestamp)
    let fixedText = text.replace(
      /\[→\]\(([a-f0-9-]+@\d+(?:\.\d+)?)\]/g,
      '[→]($1)',
    );

    // Fix regular timestamp references with bracket closing: [→](47] -> [→](47)
    fixedText = fixedText.replace(/\[→\]\((\d+(?:\.\d+)?)\]/g, '[→]($1)');

    return fixedText;
  }, []);

  // Count matches in text
  const countMatches = useCallback((text: string, term: string): number => {
    if (!term || !text) return 0;
    try {
      // Strip HTML tags before counting matches
      const cleanText = stripHtmlTags(text);

      const searchTerms = term
        .trim()
        .split(/\s+/)
        .filter((t) => t.length > 0);
      if (searchTerms.length === 0) return 0;

      const escapedTerms = searchTerms.map((t) =>
        t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
      );
      const pattern = `(${escapedTerms.join('|')})`;
      const regex = new RegExp(pattern, 'gi');

      // Count matches in clean text
      return (cleanText.match(regex) || []).length;
    } catch (error) {
      console.error('Error counting matches:', error);
      return 0;
    }
  }, []);

  // Update match count when search term changes
  useEffect(() => {
    if (resultSearchTerm && completion) {
      const count = countMatches(completion, resultSearchTerm);
      setMatchCount(count);
    } else {
      setMatchCount(0);
    }
  }, [resultSearchTerm, completion, countMatches]);

  useEffect(() => {
    if (selectedFiles.length > 0) {
      setCitationCount({});
    }
  }, [selectedFiles, setCitationCount]);

  // Add ESC key listener to clear search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && resultSearchTerm) {
        setResultSearchTerm('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [resultSearchTerm]);

  // Create markdown components with the current state
  const markdownComponents = useCallback(
    () =>
      createMarkdownComponents(
        activeSource,
        files,
        handleSourceClick,
        citationCount,
        setCitationCount,
        hideReferences,
        resultSearchTerm,
      ),
    [
      activeSource,
      files,
      handleSourceClick,
      citationCount,
      setCitationCount,
      hideReferences,
      resultSearchTerm,
    ],
  );

  // Don't return null if we're in the loading state
  if (!completion && !isLoading && stage !== 'processing') {
    return null;
  }

  return (
    <div className="max-w-3xl mx-auto">
      {completion && !isLoading && resultSearchTerm && (
        <div className="flex items-center justify-between mb-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-2xl">
          <div className="flex items-center">
            <SearchIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2" />
            <p className="text-sm text-muted-foreground">
              {matchCount > 0
                ? `Found ${matchCount} match${matchCount !== 1 ? 'es' : ''}`
                : 'No matches'}{' '}
              for{' '}
              <span className="font-bold text-blue-600 dark:text-blue-400">
                &ldquo;{resultSearchTerm}&rdquo;
              </span>
            </p>
          </div>
          <div className="flex items-center ml-auto bg-background px-2 py-0.5 rounded-lg text-xs text-muted-foreground">
            ESC to clear
          </div>
        </div>
      )}
      <div
        className={cn(
          'mb-2 bg-card border border-input rounded-2xl transition-all duration-500 ease-in-out',
          completion || isLoading
            ? 'opacity-100 scale-100'
            : 'opacity-0 scale-95 pointer-events-none',
        )}
      >
        <div
          className={cn(
            'flex items-center justify-between p-3 transition-all duration-500 ease-out overflow-hidden',
            completion && !isLoading
              ? 'opacity-100 translate-y-0 max-h-20'
              : 'opacity-0 -translate-y-4 max-h-0 p-0',
          )}
        >
          <div className="flex items-center gap-2">
            <div
              className={`relative transition-all duration-300 ${
                showSearchInput ? 'w-48 h-8' : 'w-8 h-8'
              }`}
            >
              {showSearchInput ? (
                <Input
                  type="text"
                  placeholder="Search in results..."
                  value={resultSearchTerm || ''}
                  onChange={(e) => setResultSearchTerm?.(e.target.value)}
                  onBlur={() => {
                    if (!resultSearchTerm) {
                      setShowSearchInput(false);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setResultSearchTerm?.('');
                      setShowSearchInput(false);
                      e.preventDefault();
                    }
                  }}
                  className="h-8 text-sm rounded-full pl-8 pr-8"
                  autoFocus
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setShowSearchInput(true)}
                  className="w-8 h-8 rounded-full border border-input hover:bg-accent flex items-center justify-center transition-colors"
                >
                  <SearchIcon className="h-4 w-4 text-muted-foreground" />
                </button>
              )}

              {showSearchInput && (
                <>
                  <SearchIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  {resultSearchTerm && (
                    <button
                      type="button"
                      onClick={() => {
                        setResultSearchTerm('');
                        setShowSearchInput(false);
                      }}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-blue-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onEditQuery?.()}
              className="w-8 h-8 rounded-full hover:bg-accent flex items-center justify-center"
              title="Edit query"
            >
              <Edit className="h-4 w-4 text-muted-foreground" />
            </button>
            <button
              type="button"
              onClick={() => onCopyContent?.(completion)}
              className="w-8 h-8 rounded-full hover:bg-accent flex items-center justify-center"
              title={copySuccess ? 'Copied!' : 'Copy to clipboard'}
            >
              {copySuccess ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <Copy className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
            <button
              type="button"
              onClick={() => onClearCompletion?.()}
              className="w-8 h-8 rounded-full hover:bg-accent flex items-center justify-center"
              title="Clear results"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        </div>
        <div className="p-4">
          {completion && (
            <div className="break-words w-full prose prose-sm max-w-none">
              {/* If there's a search term, pre-process the markdown text to highlight all occurrences before rendering */}
              {resultSearchTerm && completion ? (
                <div
                  // Using dangerouslySetInnerHTML is safe here because we sanitize the HTML with DOMPurify
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: processMarkdownWithHighlights(
                      fixMalformedReferences(completion),
                      resultSearchTerm || '',
                    ),
                  }}
                />
              ) : (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents()}
                >
                  {fixMalformedReferences(completion)}
                </ReactMarkdown>
              )}
            </div>
          )}

          {/* Show loading indicator when still loading but no content yet */}
          <Collapsible
            open={isLoading && stage === 'processing' && !completion}
          >
            <CollapsibleContent className="overflow-hidden transition-all duration-500 data-[state=closed]:animate-[collapsible-up_500ms_cubic-bezier(0.4,0.0,0.2,1)] data-[state=open]:animate-[collapsible-down_500ms_cubic-bezier(0.4,0.0,0.2,1)]">
              <div className="flex flex-col items-center py-6">
                <Loader className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Show a subtle streaming indicator when content is streaming */}
          {isLoading && stage === 'processing' && completion && (
            <div className="flex justify-center mt-4">
              <Loader className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Results;
